import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardList, FileWarning } from 'lucide-react';
import {
  CLASSIFICATION_LABEL,
  Classification,
  UserRole,
  type SemesterDto,
  type SemesterRowDto,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePageLabel } from '../../lib/navigation';
import { formatIndex } from '../../lib/format';
import { Alert, Badge, Button, Card, EmptyState, Skeleton } from '../../components/ui';

/**
 * `/admin/competencias/semestre/:year/:half` — média semestral (PLAN §7, tela 14).
 *
 * O filtro de meses é livre porque a apuração real às vezes precisa excluir um
 * mês atípico. Por isso a tela mostra **quais meses entraram** ao lado da média:
 * um número sem o recorte que o gerou não dá para auditar.
 *
 * Mês sem avaliação aparece como `—` e é ignorado na conta. Nunca zero: contar
 * como zero puniria o fornecedor de quem simplesmente não se comprou naquele
 * mês.
 */

const MONTH_ABBREV = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

const TONE: Record<Classification, 'success' | 'warning' | 'danger' | 'neutral'> = {
  EXCELLENT: 'success',
  GOOD: 'warning',
  UNSATISFACTORY: 'danger',
  NOT_APPLICABLE: 'neutral',
};

export function SemesterPage() {
  const { year, half } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  const [selected, setSelected] = useState<number[] | null>(null);
  const [data, setData] = useState<SemesterDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  usePageLabel(year && half ? `${half}º semestre de ${year}` : null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const query = selected?.length ? `?months=${selected.join(',')}` : '';
      setData(await api.get<SemesterDto>(`/semester/${year}/${half}${query}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar o semestre.');
    }
  }, [year, half, selected]);

  useEffect(() => {
    void load();
  }, [load]);

  async function openActionPlan(row: SemesterRowDto) {
    setBusy(row.storeId);
    setError(null);
    try {
      await api.post('/semester/action-plans', {
        storeId: row.storeId,
        year: Number(year),
        half: Number(half),
        months: selected ?? undefined,
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível abrir o plano de ação.');
    } finally {
      setBusy(null);
    }
  }

  const allMonths = Number(half) === 1 ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
  const active = selected ?? allMonths;

  function toggle(month: number) {
    const next = active.includes(month)
      ? active.filter((m) => m !== month)
      : [...active, month].sort((a, b) => a - b);
    // Desmarcar tudo devolveria uma tabela vazia sem explicação: nesse caso
    // volta ao semestre inteiro, que é o padrão.
    setSelected(next.length === 0 ? null : next);
  }

  const unsatisfactory = data?.rows.filter((row) => row.needsActionPlan) ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {half}º semestre de {year}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Média aritmética dos IQWs publicados. Mês sem avaliação é ignorado, nunca contado como
          zero (manual 5.6.1 e Nota 9).
        </p>
      </header>

      <Card>
        <p className="text-xs uppercase tracking-wide text-slate-500">Meses considerados</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {allMonths.map((month) => {
            const on = active.includes(month);
            return (
              <button
                key={month}
                type="button"
                onClick={() => toggle(month)}
                aria-pressed={on}
                className={
                  on
                    ? 'rounded-full bg-brand-600 px-3 py-1 text-sm font-medium text-white'
                    : 'rounded-full border border-slate-300 bg-white px-3 py-1 text-sm text-slate-600 hover:bg-slate-50'
                }
              >
                {MONTH_ABBREV[month - 1]}
              </button>
            );
          })}
        </div>
      </Card>

      {error && <Alert>{error}</Alert>}

      {unsatisfactory.length > 0 && (
        <Card className="border-red-200 bg-red-50/40">
          <div className="flex items-center gap-2">
            <FileWarning className="size-4 text-red-600" aria-hidden />
            <h2 className="font-medium text-slate-900">
              {unsatisfactory.length === 1
                ? '1 fornecedor fechou o semestre insatisfatório'
                : `${unsatisfactory.length} fornecedores fecharam o semestre insatisfatórios`}
            </h2>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            O RQ 230-01 §5.6.1 prevê notificação e plano de ação, com 90 dias para apresentação.
            Abrir o plano é uma decisão sua — o portal não notifica ninguém sozinho.
          </p>
        </Card>
      )}

      {!data ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : data.rows.length === 0 ? (
        <EmptyState
          title="Nenhuma competência publicada neste semestre"
          description="A média usa apenas competências publicadas — feche e publique os meses em /admin/competencias."
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[52rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-medium">Fornecedor</th>
                {data.monthLabels.map((label) => (
                  <th key={label} className="px-3 py-3 text-right font-medium">
                    {label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right font-medium">Média</th>
                <th className="px-4 py-3 font-medium">Classificação</th>
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((row) => (
                <tr key={row.storeId} className="transition-colors hover:bg-slate-50/70">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/fornecedores/${row.storeId}`}
                      className="font-medium text-brand-700 underline-offset-4 hover:underline"
                    >
                      {row.supplierName}
                    </Link>
                    <p className="font-mono text-xs text-slate-500">{row.protheusRef}</p>
                  </td>
                  {row.monthly.map((value, index) => (
                    <td
                      key={index}
                      className="px-3 py-3 text-right tabular-nums text-slate-600"
                      title={value === null ? 'Sem avaliação — ignorado na média' : undefined}
                    >
                      {value === null ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">
                    {formatIndex(row.average)}
                    <p className="text-xs font-normal text-slate-500">
                      {row.monthsConsidered} {row.monthsConsidered === 1 ? 'mês' : 'meses'}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={TONE[row.classification]}>
                      {CLASSIFICATION_LABEL[row.classification]}
                    </Badge>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      {row.needsActionPlan ? (
                        <Button
                          size="sm"
                          variant="outline"
                          loading={busy === row.storeId}
                          onClick={() => void openActionPlan(row)}
                        >
                          <ClipboardList className="size-4" aria-hidden />
                          Abrir plano de ação
                        </Button>
                      ) : row.openActionPlanId ? (
                        <span className="text-xs text-slate-500">plano já aberto</span>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
